import React, { useEffect, useMemo, useState } from 'react';
import Topbar from '../components/Topbar.jsx';
import Statusbar from '../components/Statusbar.jsx';
import StudyRow from '../components/StudyRow.jsx';
import { listStudies, listOrderingProviders } from '../api/client.js';
import styles from './Worklist.module.css';

const MODALITIES = ['', 'DX', 'CR', 'RF', 'CT', 'MR', 'US'];
const REFRESH_MS = 60_000;

export default function Worklist() {
  const [filters, setFilters] = useState({ date_from: '', date_to: '', location: '', modality: '', patient_name: '', ordering_provider: '', status: '' });
  const [providers, setProviders] = useState([]);
  const [limit]  = useState(50);
  const [offset, setOffset] = useState(0);
  const [studies, setStudies] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  async function fetchList(signal) {
    setError(null);
    try {
      const params = { limit, offset };
      for (const [k, v] of Object.entries(filters)) if (v) params[k] = v;
      const r = await listStudies(params);
      if (!signal?.aborted) setStudies(r.studies || []);
    } catch (err) {
      if (!signal?.aborted) setError(err.message || 'Failed to load');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    fetchList(ac.signal);
    return () => ac.abort();
  }, [limit, offset, JSON.stringify(filters)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const id = setInterval(() => fetchList(), REFRESH_MS);
    return () => clearInterval(id);
  }, [filters, offset]); // eslint-disable-line react-hooks/exhaustive-deps

  // Distinct ordering providers for the filter autocomplete — loaded once.
  useEffect(() => {
    listOrderingProviders().then(setProviders).catch(() => setProviders([]));
  }, []);

  const counts = useMemo(() => {
    const s = studies || [];
    return {
      total: s.length,
      none: s.filter(x => (x.ai_read_status || 'none') === 'none').length,
      prelim: s.filter(x => x.ai_read_status === 'generated').length,
      signed: s.filter(x => x.ai_read_status === 'signed').length,
    };
  }, [studies]);

  return (
    <div className={styles.shell}>
      <Topbar breadcrumb="Worklist" />

      <section className={styles.filters}>
        <input type="date" value={filters.date_from} onChange={(e) => setFilters(f => ({ ...f, date_from: e.target.value }))} />
        <input type="date" value={filters.date_to}   onChange={(e) => setFilters(f => ({ ...f, date_to:   e.target.value }))} />
        <input placeholder="Location (e.g. East LA)" value={filters.location} onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))} />
        <select value={filters.modality} onChange={(e) => setFilters(f => ({ ...f, modality: e.target.value }))}>
          {MODALITIES.map(m => <option key={m} value={m}>{m || 'Any modality'}</option>)}
        </select>
        <input placeholder="Patient name contains…" value={filters.patient_name} onChange={(e) => setFilters(f => ({ ...f, patient_name: e.target.value }))} />
        <input
          list="ordering-provider-list"
          placeholder="Ordering provider…"
          value={filters.ordering_provider}
          onChange={(e) => setFilters(f => ({ ...f, ordering_provider: e.target.value }))}
        />
        <datalist id="ordering-provider-list">
          {providers.map(p => (
            <option key={p.name} value={p.name} label={`${p.name} · ${p.study_count}`} />
          ))}
        </datalist>
        <select value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
          <option value="">Any status</option>
          <option value="received">Received</option>
          <option value="signed">Signed</option>
        </select>
      </section>

      <section className={styles.tableWrap}>
        <div className={styles.header}>
          <div>DATE</div>
          <div>PATIENT</div>
          <div>MRN</div>
          <div>MOD</div>
          <div>LOCATION</div>
          <div>DESCRIPTION</div>
          <div># IMG</div>
          <div>AI STATUS</div>
          <div></div>
        </div>

        {loading ? (
          <div className={styles.skeleton}>
            {[...Array(6)].map((_, i) => <div key={i} className={styles.skelRow} />)}
          </div>
        ) : error ? (
          <div className={styles.empty}>Error loading studies: {error}</div>
        ) : !studies || studies.length === 0 ? (
          <div className={styles.empty}>No studies found.</div>
        ) : (
          studies.map(s => <StudyRow key={s.study_instance_uid} study={s} />)
        )}

        <div className={styles.pager}>
          <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>← Prev</button>
          <span>offset {offset} · showing up to {limit}</span>
          <button disabled={(studies?.length || 0) < limit} onClick={() => setOffset(offset + limit)}>Next →</button>
        </div>
      </section>

      <Statusbar
        left={<>Studies: {counts.total}  ·  Prelim: {counts.prelim}  ·  Signed: {counts.signed}</>}
        right={<>Auto-refresh {Math.round(REFRESH_MS / 1000)}s</>}
      />
    </div>
  );
}
